// Page information data for different pages
export const PAGE_INFO_DATA = {
  'edit-modules': {
    title: 'UI Configuration Manager',
    overview: 'This page provides a centralized interface to manage the visibility and configuration of all modules in the Cognien platform. You can enable/disable individual dashboards and components, configure backend pipelines, and save your preferences for a customized user experience.',
    beforeProblem: [
      'Module configurations were scattered across multiple files and locations',
      'No visual interface to manage which modules were visible to users',
      'Difficult to enable/disable features without code changes',
      'Backend pipeline management required separate tools and manual intervention',
      'No easy way to save and restore UI configurations across deployments'
    ],
    enhancement: [
      'Unified dashboard with all module toggles in one place',
      'Visual interface to enable/disable any module with a single click',
      'Real-time configuration updates without code deployment',
      'Integrated backend pipeline management with status tracking',
      'Persistent UI configuration storage in DynamoDB',
      'Support for pre-configured solution templates from Custom Solution Builder'
    ],
    howItHelps: [
      'Reduces configuration time from hours to minutes',
      'Empowers non-technical users to manage module visibility',
      'Provides instant feedback on configuration changes',
      'Centralizes all configuration management in one intuitive interface',
      'Eliminates need for manual code editing for simple visibility changes',
      'Enables quick A/B testing of different module combinations',
      'Supports multi-tenant deployments with different module sets'
    ],
    keyFeatures: [
      '🎛️ Toggle switches for each module category (WNT, Cybershield, Net Zero, etc.)',
      '💾 Save UI Configuration to persist visibility settings',
      '🔄 Backend Pipeline integration for configuration deployment',
      '📊 Real-time status tracking for pipeline executions',
      '🎯 Pre-enable module support from Deployed Solutions',
      '🔐 Secure configuration storage in AWS DynamoDB',
      '📱 Responsive design for desktop and tablet devices',
      '✨ Drag-and-drop pipeline status banners',
      '🔍 Clear visual feedback for all actions'
    ]
  },

  'stack-recommender': {
    title: 'AI Stack Recommender',
    overview: 'Get intelligent recommendations for technology stacks and module combinations based on your requirements. Use AI to analyze your needs and suggest the optimal configuration.',
    beforeProblem: [
      'Choosing the right modules required deep technical knowledge',
      'No guidance for optimal module combinations',
      'Trial and error approach wasted time and resources',
      'Difficulty matching customer requirements to available modules',
      'No best practices or recommendations available'
    ],
    enhancement: [
      'AI-powered recommendation engine',
      'Natural language requirement input',
      'Smart analysis of use cases',
      'Automated module matching',
      'Best practice suggestions',
      'Confidence scores for recommendations'
    ],
    howItHelps: [
      'Faster solution design with AI assistance',
      'Better module selection based on requirements',
      'Reduced risk of incompatible combinations',
      'Learn best practices through recommendations',
      'Improved customer satisfaction with optimized solutions',
      'Accelerated sales cycle with quick proposals'
    ],
    keyFeatures: [
      '🤖 AI-powered recommendations',
      '💬 Natural language processing',
      '🎯 Requirement analysis',
      '📊 Confidence scoring',
      '🔄 Alternative suggestions',
      '📚 Best practices library',
      '⚡ Quick proposal generation',
      '🎨 Visual stack diagrams'
    ]
  },

  'dashboard': {
    title: 'Demand Response Dashboard',
    overview: 'Comprehensive real-time monitoring and analytics dashboard for demand response and energy optimization. View key DR metrics, peak demand trends, and load management alerts.',
    beforeProblem: [
      'DR data scattered across multiple systems',
      'No real-time visibility into demand response events',
      'Manual DR program participation tracking',
      'Delayed response to peak demand events',
      'Difficult to quantify DR savings and performance'
    ],
    enhancement: [
      'Unified DR monitoring dashboard',
      'Interactive demand visualizations',
      'Automated DR event tracking',
      'Real-time peak demand alerts',
      'DR performance analytics',
      'Customizable DR widget layouts'
    ],
    howItHelps: [
      'Instant visibility into DR program performance',
      'Quick response to demand events',
      'Data-driven DR strategy decisions',
      'Proactive peak load management',
      'Maximized DR incentive earnings',
      'Better grid participation and planning'
    ],
    keyFeatures: [
      '📊 Real-time DR event visualization',
      '⚡ Live demand metrics and forecasts',
      '📈 Peak demand trend analysis',
      '🔔 DR event alerts and notifications',
      '🎯 DR KPI tracking and reporting',
      '📱 Responsive design for all devices',
      '🔄 Auto-refresh and live updates',
      '💾 Historical DR performance data'
    ]
  },

  'em': {
    title: 'Energy Management Dashboard',
    overview: 'Comprehensive real-time monitoring and analytics dashboard for energy management systems. View key energy metrics, consumption trends, and optimization alerts.',
    beforeProblem: [
      'Energy data scattered across multiple systems',
      'No real-time visibility into energy consumption',
      'Manual energy data collection and reporting',
      'Delayed response to energy anomalies',
      'Difficult to identify optimization opportunities'
    ],
    enhancement: [
      'Unified real-time energy dashboard',
      'Interactive energy data visualizations',
      'Automated data collection and refresh',
      'Real-time energy alerts and notifications',
      'Energy trend analysis and forecasting',
      'Customizable energy widget layouts'
    ],
    howItHelps: [
      'Instant visibility into energy performance',
      'Quick identification of energy issues and anomalies',
      'Data-driven energy management decisions',
      'Proactive energy optimization',
      'Reduced energy costs through smart management',
      'Better energy resource allocation and planning'
    ],
    keyFeatures: [
      '📊 Real-time energy data visualization',
      '⚡ Live energy consumption metrics',
      '📈 Energy trend analysis and forecasting',
      '🔔 Smart energy alerts and notifications',
      '🎯 Energy KPI tracking and monitoring',
      '📱 Responsive design for all devices',
      '🔄 Auto-refresh and live updates',
      '💾 Historical energy data analysis'
    ]
  },

  'net-zero': {
    title: 'Net Zero Dashboard',
    overview: 'Track and manage your journey towards net-zero carbon emissions. Monitor carbon footprint, renewable energy usage, and sustainability goals with comprehensive analytics.',
    beforeProblem: [
      'No unified way to track carbon emissions',
      'Manual calculation of carbon footprint',
      'Difficult to measure progress towards net-zero goals',
      'Limited visibility into renewable energy contribution',
      'No actionable insights for emission reduction'
    ],
    enhancement: [
      'Automated carbon footprint calculation',
      'Real-time emission tracking',
      'Renewable energy percentage monitoring',
      'Goal setting and progress tracking',
      'Predictive analytics for net-zero achievement',
      'Detailed emission source breakdown'
    ],
    howItHelps: [
      'Clear visibility into carbon footprint',
      'Track progress towards sustainability goals',
      'Identify high-emission areas for improvement',
      'Demonstrate environmental commitment',
      'Optimize renewable energy usage',
      'Generate sustainability reports easily'
    ],
    keyFeatures: [
      '🌱 Carbon footprint tracking',
      '♻️ Renewable energy monitoring',
      '🎯 Net-zero goal management',
      '📊 Emission analytics and trends',
      '📈 Predictive carbon modeling',
      '🔍 Source-level emission breakdown',
      '📄 Sustainability reporting',
      '💡 Reduction recommendations'
    ]
  },

  'wnt': {
    title: 'Wide Area Network Twin',
    overview: 'Digital twin visualization and management for wide-area network infrastructure. Monitor, simulate, and optimize your electrical network with advanced 3D modeling and real-time data integration.',
    beforeProblem: [
      'No digital representation of network infrastructure',
      'Difficult to visualize network topology',
      'Manual network planning and analysis',
      'Limited scenario simulation capabilities',
      'Hard to identify network bottlenecks'
    ],
    enhancement: [
      '3D digital twin visualization',
      'Real-time network data integration',
      'Interactive single-line diagrams',
      'Scenario simulation and what-if analysis',
      'Network optimization recommendations',
      'Geographic network mapping'
    ],
    howItHelps: [
      'Better understanding of network topology',
      'Improved network planning and design',
      'Quick identification of issues',
      'Risk-free scenario testing',
      'Optimized network operations',
      'Enhanced maintenance planning'
    ],
    keyFeatures: [
      '🗺️ 3D digital twin visualization',
      '⚡ Real-time network monitoring',
      '📐 Single-line diagram (SLD) view',
      '🌍 Geographic mapping',
      '🔮 Scenario simulation',
      '🎯 Network optimization',
      '📊 Performance analytics',
      '🔄 Live data synchronization'
    ]
  },

  'cybershield': {
    title: 'CyberShield Security Dashboard',
    overview: 'Comprehensive cybersecurity monitoring and threat detection for critical energy infrastructure. Real-time security posture assessment and risk management.',
    beforeProblem: [
      'Limited visibility into security threats',
      'Manual security monitoring',
      'Delayed threat detection',
      'No centralized security dashboard',
      'Difficult to assess overall security posture'
    ],
    enhancement: [
      'Real-time threat monitoring',
      'Automated security posture assessment',
      'Risk Intelligence Module (RIM)',
      'Security incident tracking',
      'Compliance monitoring',
      'Threat analytics and reporting'
    ],
    howItHelps: [
      'Proactive threat detection and response',
      'Improved security posture',
      'Reduced security incident response time',
      'Better compliance management',
      'Enhanced infrastructure protection',
      'Data-driven security decisions'
    ],
    keyFeatures: [
      '🛡️ Real-time threat monitoring',
      '🎯 Security posture assessment',
      '📊 Risk Intelligence Module (RIM)',
      '🔔 Automated alert system',
      '📈 Security analytics',
      '✅ Compliance tracking',
      '🔍 Incident investigation tools',
      '📄 Security reporting'
    ]
  },

  'microgrid-opt': {
    title: 'Microgrid Optimization',
    overview: 'Optimize microgrid operations with advanced algorithms for energy dispatch, storage management, and cost minimization. Maximize efficiency and reliability of distributed energy resources.',
    beforeProblem: [
      'Manual microgrid operation and control',
      'Suboptimal energy dispatch decisions',
      'No automated optimization',
      'High operational costs',
      'Inefficient use of storage and renewables'
    ],
    enhancement: [
      'AI-powered optimization algorithms',
      'Automated energy dispatch',
      'Battery storage optimization',
      'Load forecasting and management',
      'Cost minimization strategies',
      'Real-time operational adjustments'
    ],
    howItHelps: [
      'Reduced operational costs',
      'Improved energy efficiency',
      'Better utilization of renewable energy',
      'Optimal battery storage management',
      'Enhanced grid reliability',
      'Automated decision-making'
    ],
    keyFeatures: [
      '⚡ Automated energy dispatch',
      '🔋 Battery storage optimization',
      '📊 Load forecasting',
      '💰 Cost optimization',
      '🌞 Renewable integration',
      '📈 Performance analytics',
      '🎯 Multi-objective optimization',
      '🔄 Real-time adjustments'
    ]
  },

  'solar-energy': {
    title: 'Solar Energy Management',
    overview: 'Monitor and optimize solar energy generation with advanced analytics, performance tracking, and predictive maintenance. Maximize solar asset ROI with data-driven insights.',
    beforeProblem: [
      'Limited visibility into solar performance',
      'Manual monitoring of solar arrays',
      'Reactive maintenance approach',
      'Difficult to identify underperforming panels',
      'No prediction of generation patterns'
    ],
    enhancement: [
      'Real-time solar generation monitoring',
      'Performance analytics and benchmarking',
      'Predictive generation forecasting',
      'Automated anomaly detection',
      'Maintenance scheduling optimization',
      'Panel-level diagnostics'
    ],
    howItHelps: [
      'Maximized solar energy production',
      'Reduced maintenance costs',
      'Quick identification of issues',
      'Improved asset performance',
      'Better generation forecasting',
      'Increased ROI on solar investments'
    ],
    keyFeatures: [
      '☀️ Real-time generation monitoring',
      '📊 Performance analytics',
      '🔮 Generation forecasting',
      '🔍 Anomaly detection',
      '🔧 Predictive maintenance',
      '📈 Efficiency tracking',
      '🌡️ Environmental monitoring',
      '💰 ROI analysis'
    ]
  },

  'proofer-tool': {
    title: 'Proofer Analysis Tool',
    overview: 'Advanced power system analysis and validation tool for electrical network design and operation. Verify network configurations and ensure optimal performance.',
    beforeProblem: [
      'Manual network analysis and validation',
      'Time-consuming calculation processes',
      'Limited scenario testing',
      'Risk of configuration errors',
      'Difficult to verify network designs'
    ],
    enhancement: [
      'Automated network analysis',
      'Fast calculation engine',
      'Scenario testing capabilities',
      'Validation and error checking',
      'Comprehensive reporting',
      'Standards compliance verification'
    ],
    howItHelps: [
      'Faster network analysis',
      'Reduced design errors',
      'Better network validation',
      'Improved design confidence',
      'Standardized analysis approach',
      'Time savings in engineering'
    ],
    keyFeatures: [
      '⚡ Automated network analysis',
      '🔍 Configuration validation',
      '📊 Multiple scenario testing',
      '✅ Standards compliance check',
      '📈 Performance predictions',
      '📄 Detailed reporting',
      '🎯 Error detection',
      '💾 Configuration management'
    ]
  },

  'evci-eds': {
    title: 'EVCI Energy Delivery System',
    overview: 'Manage and optimize Electric Vehicle Charging Infrastructure with intelligent load management, billing integration, and grid interaction capabilities.',
    beforeProblem: [
      'Unmanaged EV charging loads',
      'No visibility into charging patterns',
      'Grid overload risks',
      'Manual billing and management',
      'Limited charging optimization'
    ],
    enhancement: [
      'Smart charging load management',
      'Real-time charger monitoring',
      'Grid-aware charging optimization',
      'Automated billing integration',
      'Usage analytics and reporting',
      'Demand response capabilities'
    ],
    howItHelps: [
      'Prevent grid overload',
      'Optimize charging costs',
      'Better user experience',
      'Automated billing and payments',
      'Efficient energy utilization',
      'Support for renewable integration'
    ],
    keyFeatures: [
      '🔌 Smart charging management',
      '⚡ Load balancing',
      '📊 Usage analytics',
      '💳 Billing integration',
      '🌐 Grid interaction',
      '🔋 Battery optimization',
      '📱 User management',
      '📈 Revenue tracking'
    ]
  }
};
